import React from 'react';
import { useT } from '../../context/LanguageContext';

/**
 * The kiosk's attract screen with nothing booked on it yet.
 *
 * A branch that has uploaded no posters or clips used to show a thali picture
 * shipped inside the app — one restaurant's dish, on every kiosk of every
 * restaurant, which is somebody else's lunch advertised on your counter. This
 * stands there instead: a monster whose eyes wander the room, holding the
 * slot open and saying what it is for.
 *
 * Drawn rather than drawn-from-a-file: one SVG costs no download, scales to a
 * 43-inch panel without going soft, and takes its colours from the kiosk's own
 * tokens so it belongs to the screen it is standing on. Everything that moves
 * is CSS (see kiosk.css, `.k-adslot-*`), so it stops dead under
 * prefers-reduced-motion.
 */
export default function AdSlotMonster() {
  const { t } = useT();
  return (
    <div className="k-adslot">
      <svg className="k-adslot-art" viewBox="0 0 320 300" role="img"
        aria-label={t('kiosk.adslot.alt')}>
        {/* The ground he stands on, so he is not floating in space. */}
        <ellipse className="k-adslot-shadow" cx="160" cy="268" rx="78" ry="12" />

        <g className="k-adslot-body">
          {/* Antennae, with a bulb each — they lag behind the bob, which is
              what makes a drawn thing look like it has weight. */}
          <g className="k-adslot-antenna">
            <path d="M126 92 C118 62 104 52 96 44" />
            <circle cx="94" cy="40" r="11" />
          </g>
          <g className="k-adslot-antenna k-adslot-antenna-b">
            <path d="M194 92 C202 62 216 52 224 44" />
            <circle cx="226" cy="40" r="11" />
          </g>

          {/* The body: a soft blob, wider at the foot. */}
          <path className="k-adslot-skin"
            d="M160 72 C222 72 252 118 252 170 C252 226 216 258 160 258
               C104 258 68 226 68 170 C68 118 98 72 160 72 Z" />

          {/* Arms. The right one waves; the left holds the slot open. */}
          <g className="k-adslot-arm-l">
            <path className="k-adslot-limb" d="M74 168 C48 176 38 196 42 214" />
          </g>
          <g className="k-adslot-arm-r">
            <path className="k-adslot-limb" d="M246 168 C272 160 288 140 286 120" />
          </g>

          {/* Eyes. The whites blink; the pupils wander — the two are separate
              groups so a blink never drags the pupil with it. */}
          <g className="k-adslot-blink">
            <ellipse className="k-adslot-eye" cx="128" cy="158" rx="30" ry="33" />
            <ellipse className="k-adslot-eye" cx="196" cy="158" rx="30" ry="33" />
            <g className="k-adslot-pupils">
              <circle className="k-adslot-pupil" cx="128" cy="160" r="13" />
              <circle className="k-adslot-pupil" cx="196" cy="160" r="13" />
              {/* The catchlight is what stops an eye looking dead. */}
              <circle className="k-adslot-glint" cx="133" cy="154" r="4.5" />
              <circle className="k-adslot-glint" cx="201" cy="154" r="4.5" />
            </g>
          </g>

          <circle className="k-adslot-cheek" cx="98" cy="196" r="12" />
          <circle className="k-adslot-cheek" cx="226" cy="196" r="12" />
          {/* A grin, open — a closed curve reads as a smirk at this size. */}
          <path className="k-adslot-mouth" d="M132 200 C146 220 178 220 192 200 Z" />

          <path className="k-adslot-limb" d="M126 258 L126 272" />
          <path className="k-adslot-limb" d="M194 258 L194 272" />
        </g>
      </svg>

      <div className="k-adslot-say">
        <span className="k-adslot-head">{t('kiosk.adslot.title')}</span>
        <span className="k-adslot-sub">{t('kiosk.adslot.sub')}</span>
      </div>
    </div>
  );
}
